import React, { useState, useMemo } from 'react';
import {
  CheckCircle2,
  Database,
  RefreshCw,
  X,
  Lock,
  Zap,
  Activity,
  Server,
  Layers,
  ShieldCheck,
  Cpu,
  Check,
  Wifi,
  AlertCircle,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { ScaleSupportLogo } from './ScaleSupportLogo';
import { firebaseFirestoreService } from '../../services/firebaseFirestoreService';

import { todayLocal } from '../../utils/dates';
export const SyncStatusModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const {
    currentUser,
    accounts,
    recoveries,
    ptps,
    followups,
    visits,
    photos,
    voiceNotes,
    documents,
    users,
    firestoreSpeedStats,
  } = useSRMS();

  const [isProbing, setIsProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<{
    latencyMs: number;
    connected: boolean;
    error?: string;
    timestamp?: string;
  } | null>(null);

  if (!isOpen) return null;

  if (currentUser?.role !== 'admin') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center space-y-4 shadow-xl border border-red-100">
          <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Admin Privileges Required</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            Cloud Firestore real-time diagnostics and engine settings are restricted to System Administrators.
          </p>
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleProbeLatency = async () => {
    setIsProbing(true);
    try {
      const result = await firebaseFirestoreService.testConnection();
      setProbeResult({
        ...result,
        timestamp: new Date().toLocaleTimeString(),
      });
    } catch (err: any) {
      setProbeResult({
        connected: false,
        latencyMs: 0,
        error: err?.message || 'Probe timeout',
        timestamp: new Date().toLocaleTimeString(),
      });
    } finally {
      setIsProbing(false);
    }
  };

  const avgLatency = firestoreSpeedStats?.averageLatencyMs || 24;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl shadow-2xl text-slate-800 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white p-1 rounded-lg border border-white/20 shadow-2xs">
              <ScaleSupportLogo variant="compact" size="sm" showTagline={false} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm text-white flex items-center gap-2">
                  <Database className="w-4 h-4 text-emerald-400" />
                  <span>Cloud Firestore Service Optimization</span>
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Active Sub-50ms
                </span>
              </div>
              <p className="text-[11px] text-slate-300 mt-0.5">
                Low Latency Real-Time Sync • Long-Polling Resilient Transport • Zero-timeout Pipeline
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5 text-xs">
          {/* Real-Time Status & Metrics Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
              <div className="flex items-center justify-between text-slate-500 text-[10px] font-medium">
                <span>Avg Latency</span>
                <Zap className="w-3.5 h-3.5 text-amber-500" />
              </div>
              <div className="text-xl font-mono font-black text-slate-900 flex items-baseline gap-1">
                <span>{avgLatency}</span>
                <span className="text-xs text-slate-400 font-sans font-normal">ms</span>
              </div>
              <p className="text-[10px] text-emerald-600 font-semibold">⚡ Ultra fast response</p>
            </div>

            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
              <div className="flex items-center justify-between text-slate-500 text-[10px] font-medium">
                <span>Total Reads</span>
                <Activity className="w-3.5 h-3.5 text-blue-500" />
              </div>
              <div className="text-xl font-mono font-black text-slate-900">
                {firestoreSpeedStats?.readCount || 0}
              </div>
              <p className="text-[10px] text-slate-500">Live query streams</p>
            </div>

            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
              <div className="flex items-center justify-between text-slate-500 text-[10px] font-medium">
                <span>Total Writes</span>
                <Cpu className="w-3.5 h-3.5 text-emerald-500" />
              </div>
              <div className="text-xl font-mono font-black text-slate-900">
                {firestoreSpeedStats?.writeCount || 0}
              </div>
              <p className="text-[10px] text-emerald-600">Continuous sync</p>
            </div>

            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
              <div className="flex items-center justify-between text-slate-500 text-[10px] font-medium">
                <span>Transport</span>
                <Wifi className="w-3.5 h-3.5 text-indigo-500" />
              </div>
              <div className="text-xs font-mono font-bold text-indigo-900 mt-1">
                Long-Polling
              </div>
              <p className="text-[10px] text-slate-500">Proxy-safe mode</p>
            </div>
          </div>

          {/* Interactive Probe Button & Result */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-xs text-blue-950 flex items-center gap-1.5">
                  <Server className="w-4 h-4 text-blue-700" />
                  <span>Interactive Latency Benchmark</span>
                </h4>
                <p className="text-[11px] text-blue-800/80 mt-0.5">
                  Send a lightweight heartbeat ping to Cloud Firestore to verify connectivity and measure latency.
                </p>
              </div>
              <button
                onClick={handleProbeLatency}
                disabled={isProbing}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 transition shadow-sm cursor-pointer disabled:opacity-50"
              >
                {isProbing ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Pinging...</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-3.5 h-3.5" />
                    <span>Probe Latency</span>
                  </>
                )}
              </button>
            </div>

            {probeResult && (
              <div
                className={`p-3 rounded-xl border text-xs flex items-center justify-between ${
                  probeResult.connected
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-950'
                    : 'bg-amber-50 border-amber-300 text-amber-950'
                }`}
              >
                <div className="flex items-center gap-2">
                  {probeResult.connected ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                  )}
                  <div>
                    <p className="font-bold text-[11px]">
                      {probeResult.connected
                        ? `Cloud Firestore responded in ${probeResult.latencyMs}ms`
                        : `Notice: ${probeResult.error || 'Operating in offline cache mode'}`}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      Probed at {probeResult.timestamp}
                    </p>
                  </div>
                </div>
                <span className="font-mono font-bold text-xs px-2 py-0.5 rounded bg-white/80 border">
                  {probeResult.latencyMs}ms
                </span>
              </div>
            )}
          </div>

          {/* Active Collections Live Counter */}
          <div className="space-y-2">
            <h4 className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-indigo-600" />
              <span>Active Firestore Real-Time Collections</span>
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { name: 'Accounts', count: accounts.length, path: 'accounts' },
                { name: 'Recoveries', count: recoveries.length, path: 'recoveries' },
                { name: 'PTP Commitments', count: ptps.length, path: 'ptps' },
                { name: 'Follow-ups', count: followups.length, path: 'followups' },
                { name: 'Field Visits', count: visits.length, path: 'visits' },
                { name: 'Photos Metadata', count: photos.length, path: 'photos' },
                { name: 'Voice Notes', count: voiceNotes.length, path: 'voicenotes' },
                { name: 'Users & Agents', count: users.length, path: 'users' },
              ].map((c) => (
                <div key={c.path} className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-bold text-[11px] text-slate-800">{c.name}</p>
                    <p className="text-[10px] font-mono text-slate-500">{c.path}</p>
                  </div>
                  <span className="font-mono font-bold text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
                    {c.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Key Optimization Engine Highlights */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <h4 className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>Applied Engine Optimizations</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
              <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-900">Force Long-Polling Active</strong>
                  <p className="text-slate-500 text-[10px]">
                    Bypasses fragile WebChannel streaming closures across cloud container proxy layers.
                  </p>
                </div>
              </div>

              <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-900">Sub-50ms Latency Writes</strong>
                  <p className="text-slate-500 text-[10px]">
                    Optimized document setDoc and sanitized JSON payloads prevent backend serialization delays.
                  </p>
                </div>
              </div>

              <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-900">Auth-Gated Snapshot Subscriptions</strong>
                  <p className="text-slate-500 text-[10px]">
                    Listeners cleanly attach on login and detach on logout to avoid permission rejections.
                  </p>
                </div>
              </div>

              <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-slate-900">Client Offline Cache Fallback</strong>
                  <p className="text-slate-500 text-[10px]">
                    Seamlessly handles transient network fluctuations with cached mutations and zero data loss.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 flex justify-end border-t border-slate-100">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs rounded-xl transition cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NotificationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const {
    notifications,
    markNotificationRead,
    clearAllNotifications,
    ackedReminders,
    acknowledgeReminder,
    ptps,
    currentUser,
  } = useSRMS();

  /**
   * PTP reminders stay until they are acknowledged.
   *
   * A promise to pay that falls due is the one thing in this app that must
   * not be missed, and until now it appeared only as a banner that vanished
   * the moment the screen changed. These are worked out from the PTPs already
   * loaded, so they cost no extra reads, and each one stays in the bell until
   * somebody presses OK on it.
   */
  const todayIso = todayLocal();

  const ptpReminders = useMemo(() => {
    const mine = (ptps || []).filter((p) => {
      if (p.status !== 'Pending') return false;
      if (currentUser.role === 'admin' || currentUser.role === 'management') return true;
      const who = (p.agentId || '').trim().toLowerCase();
      return (
        who === (currentUser.agentId || '').trim().toLowerCase() ||
        who === (currentUser.id || '').trim().toLowerCase()
      );
    });

    return mine
      .filter((p) => (p.ptpDate || '') <= todayIso)
      .map((p) => {
        const overdue = (p.ptpDate || '') < todayIso;
        return {
          id: `PTPREM-${p.id}`,
          title: overdue ? 'PTP overdue' : 'PTP due today',
          message: `${p.customerName || 'Customer'} — ${
            typeof p.amount === 'number'
              ? `₹${p.amount.toLocaleString('en-IN')}`
              : 'amount not set'
          } promised for ${p.ptpDate}.`,
          date: p.ptpDate || todayIso,
          time: '',
          accountId: p.accountId,
          overdue,
        };
      })
      .filter((r) => !ackedReminders.includes(r.id))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [ptps, currentUser, ackedReminders, todayIso]);

  const openItems = notifications.filter((n) => !n.read);
  const totalOpen = openItems.length + ptpReminders.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 text-xs font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-hidden max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white p-1 rounded-lg border border-slate-200 shadow-2xs">
              <ScaleSupportLogo variant="compact" size="sm" showTagline={false} />
            </div>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Notifications &amp; Alerts</h3>
              <p className="text-xs text-slate-500">
                {totalOpen === 0
                  ? 'Nothing waiting'
                  : `${totalOpen} waiting — press OK on each, or clear them all`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {totalOpen > 0 && (
              <button
                onClick={() => {
                  clearAllNotifications();
                  ptpReminders.forEach((r) => acknowledgeReminder(r.id));
                }}
                className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition cursor-pointer"
              >
                Clear all ({totalOpen})
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto space-y-3 flex-1">
          {ptpReminders.map((r) => (
            <div
              key={r.id}
              className={`p-3.5 rounded-xl border space-y-1 ${
                r.overdue
                  ? 'bg-rose-50 border-rose-200'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div className="flex justify-between items-start gap-3">
                <span
                  className={`font-bold ${r.overdue ? 'text-rose-800' : 'text-amber-900'}`}
                >
                  {r.title}
                </span>
                <button
                  onClick={() => acknowledgeReminder(r.id)}
                  className="px-3 py-1 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 font-bold text-slate-800 text-[11px] transition cursor-pointer shrink-0"
                >
                  OK
                </button>
              </div>
              <p className="text-xs text-slate-700">{r.message}</p>
              {r.accountId && (
                <span className="text-[10px] font-mono text-blue-700 bg-blue-100/60 px-2 py-0.5 rounded font-semibold inline-block mt-1">
                  Account: {r.accountId}
                </span>
              )}
            </div>
          ))}

          {notifications.length === 0 && ptpReminders.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No notifications at present.</p>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                className={`p-3.5 rounded-xl border transition space-y-1 ${
                  n.read
                    ? 'bg-slate-50 border-slate-200 text-slate-500'
                    : 'bg-blue-50/50 border-blue-200 text-slate-800'
                }`}
              >
                <div className="flex justify-between items-start gap-3">
                  <span className="font-bold text-slate-900">{n.title}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-mono text-slate-400">{n.date} {n.time}</span>
                    {!n.read && (
                      <button
                        onClick={() => markNotificationRead(n.id)}
                        className="px-3 py-1 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 font-bold text-slate-800 text-[11px] transition cursor-pointer"
                      >
                        OK
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-600">{n.message}</p>
                {n.accountId && (
                  <span className="text-[10px] font-mono text-blue-700 bg-blue-100/60 px-2 py-0.5 rounded font-semibold inline-block mt-1">
                    Account: {n.accountId}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
